import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, getSessionId, euro, WHATSAPP_NUMBERS } from "../lib/api";
import { track } from "../lib/track";
import { Header } from "../components/Header";
import { ContactWidget } from "../components/ContactWidget";
import { StatusBadge } from "../components/StatusBadge";
import { UrgencySignals } from "../components/UrgencySignals";
import { Wizard } from "../components/Wizard";
import { toast } from "sonner";
import {
  Heart, Gauge, Fuel, Calendar, Cog, Check, ArrowLeft, Lock, MessageCircle,
  ChevronRight, Loader2, AlertTriangle, Sparkles,
} from "lucide-react";

function Countdown({ expiresAt }) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setLeft(Math.max(0, Math.floor((new Date(expiresAt) - new Date()) / 1000)));
    }, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);
  if (left <= 0) return null;
  const m = String(Math.floor(left / 60)).padStart(2, "0");
  const s = String(left % 60).padStart(2, "0");
  return <span className="font-mono">{m}:{s}</span>;
}

export default function VehicleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [v, setV] = useState(null);
  const [config, setConfig] = useState({});
  const [stats, setStats] = useState({ signals: [], active_block: null });
  const [fav, setFav] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const galleryRef = useRef(null);

  const load = () => {
    api.get(`/vehicles/${id}`).then((r) => setV(r.data)).catch(() => nav("/"));
    api.get(`/vehicles/${id}/stats?session_id=${getSessionId()}`).then((r) => setStats(r.data));
  };

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data));
    load();
    api.post(`/vehicles/${id}/view`, { session_id: getSessionId() });
    const beat = setInterval(() => {
      api.post(`/vehicles/${id}/view`, { session_id: getSessionId() });
      api.get(`/vehicles/${id}/stats?session_id=${getSessionId()}`).then((r) => setStats(r.data));
    }, 12000);
    return () => clearInterval(beat);
    // eslint-disable-next-line
  }, [id]);

  // deposit return handling
  useEffect(() => {
    const dep = params.get("deposit");
    const sid = params.get("session_id");
    if (dep === "success" && sid) {
      toast.loading("Verifica del pagamento in corso...", { id: "pay" });
      let attempts = 0;
      const poll = async () => {
        attempts++;
        try {
          const { data } = await api.get(`/payments/status/${sid}`);
          if (data.payment_status === "paid") {
            toast.success("Deposito ricevuto! Vettura bloccata.", { id: "pay" });
            load();
            return;
          }
          if (data.status === "expired" || attempts >= 6) {
            toast.error("Pagamento non completato.", { id: "pay" });
            return;
          }
          setTimeout(poll, 2000);
        } catch { toast.error("Errore verifica pagamento.", { id: "pay" }); }
      };
      poll();
      params.delete("deposit"); params.delete("session_id"); setParams(params, { replace: true });
    } else if (dep === "cancel") {
      toast.info("Blocco annullato.");
      params.delete("deposit"); setParams(params, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const toggleFav = async () => {
    const next = !fav; setFav(next);
    await api.post(`/vehicles/${id}/favorite`, { session_id: getSessionId(), action: next ? "add" : "remove" });
    api.get(`/vehicles/${id}/stats?session_id=${getSessionId()}`).then((r) => setStats(r.data));
  };

  if (!v) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-ink-soft" /></div>;

  const otherBlocking = stats.active_block && stats.active_block.by_session !== getSessionId();
  const sold = v.status === "venduta";

  const specs = [
    { icon: Calendar, k: "Anno", val: v.specs?.registration || v.year },
    { icon: Gauge, k: "Km", val: Number(v.km).toLocaleString("it-IT") },
    { icon: Fuel, k: "Alimentazione", val: v.fuel },
    { icon: Cog, k: "Cambio", val: v.transmission },
  ];
  const sp = v.specs || {};
  const extraSpecs = [
    ["Carrozzeria", sp.body], ["Potenza", sp.kw ? `${sp.kw} kW (${Math.round(sp.kw * 1.36)} CV)` : null],
    ["Cilindrata", sp.cc ? `${sp.cc} cc` : null], ["Trazione", sp.traction], ["Porte / Posti", sp.doors ? `${sp.doors} / ${sp.seats}` : null],
    ["Classe emissioni", sp.emission_class ? `Euro ${sp.emission_class}` : null], ["Colore esterno", sp.exterior_color], ["Interni", sp.interior],
    ["Condizione", sp.usage], ["Garanzia", sp.warranty_months ? `${sp.warranty_months} mesi` : null],
  ].filter(([, val]) => val);

  return (
    <div className="min-h-screen pb-28">
      <Header />

      {/* Gallery */}
      <div className="pt-20">
        <div ref={galleryRef}
          onScroll={(e) => setActiveIdx(Math.round(e.target.scrollLeft / e.target.clientWidth))}
          className="flex overflow-x-auto snap-x-mandatory hide-scrollbar">
          {(v.images || []).map((img, i) => (
            <div key={i} className="relative min-w-full snap-center aspect-[16/10] md:aspect-[21/9] bg-secondary">
              <img src={img} alt={`${v.model} ${i}`} onError={(e) => { e.currentTarget.src = "/placeholder-car.jpg"; }} className="w-full h-full object-cover" />
              {v.images_missing && (
                <div data-testid="photo-updating-detail" className="absolute bottom-4 left-4 px-3 py-1.5 rounded-full bg-ink/80 text-white text-[11px] uppercase tracking-[0.15em] backdrop-blur">Foto in aggiornamento</div>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-center gap-1.5 -mt-6 relative">
          {(v.images || []).map((_, i) => (
            <span key={i} className={`h-1.5 rounded-full transition-all ${i === activeIdx ? "w-6 bg-white" : "w-1.5 bg-white/50"}`} />
          ))}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 md:px-8 -mt-4">
        <button onClick={() => nav("/")} data-testid="back-home" className="inline-flex items-center gap-1.5 text-sm text-ink-soft mt-6 hover:text-ink transition-colors">
          <ArrowLeft size={15} strokeWidth={1.5} /> Catalogo
        </button>

        {/* Header block */}
        <div className="flex items-start justify-between mt-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-sand">{v.make}</div>
            <h1 className="font-serif text-4xl md:text-5xl font-light leading-tight mt-1">{v.model}</h1>
            <p className="text-ink-soft mt-1">{v.version}</p>
          </div>
          <button data-testid="favorite-btn" onClick={toggleFav}
            className="w-12 h-12 rounded-full border border-border bg-white flex items-center justify-center hover:border-clay transition-colors">
            <motion.span animate={{ scale: fav ? [1, 1.3, 1] : 1 }} transition={{ duration: 0.3 }}>
              <Heart size={20} strokeWidth={1.5} className={fav ? "fill-clay text-clay" : "text-ink-soft"} />
            </motion.span>
          </button>
        </div>

        <div className="mt-4"><StatusBadge status={v.status} /></div>

        {/* Price */}
        <div className="flex items-end justify-between mt-6 pb-6 border-b border-border">
          <div>
            <div className="font-serif text-5xl">{euro(v.price)}</div>
            {v.monthly_rate && <div className="text-sm text-ink-soft mt-1">oppure da <span className="text-ink font-medium">{euro(v.monthly_rate)}/mese</span></div>}
          </div>
        </div>

        {/* Urgency + alerts */}
        <div className="mt-6 space-y-3">
          <AnimatePresence>
            {otherBlocking && (
              <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                data-testid="block-alert" className="flex items-center gap-3 p-4 rounded-2xl bg-clay/10 border border-clay/25 text-clay">
                <AlertTriangle size={18} strokeWidth={1.5} />
                <div className="text-sm">
                  <div className="font-medium">Un altro cliente sta bloccando questa vettura</div>
                  {stats.active_block?.expires_at && <div className="text-xs opacity-80">Si libera tra <Countdown expiresAt={stats.active_block.expires_at} /></div>}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <UrgencySignals signals={stats.signals} />
        </div>

        {/* Specs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8">
          {specs.map((s, i) => (
            <div key={i} className="p-4 bg-white rounded-2xl shadow-ambient">
              <s.icon size={18} strokeWidth={1.5} className="text-sand" />
              <div className="text-[10px] uppercase tracking-wider text-ink-soft mt-2">{s.k}</div>
              <div className="text-sm font-medium mt-0.5">{s.val}</div>
            </div>
          ))}
        </div>

        {extraSpecs.length > 0 && (
          <div data-testid="vehicle-extra-specs" className="mt-6 bg-white rounded-2xl shadow-ambient p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {extraSpecs.map(([k, val]) => (
              <div key={k} className="flex justify-between gap-3 border-b border-border/60 py-1.5"><span className="text-ink-soft">{k}</span><span className="font-medium text-right">{val}</span></div>
            ))}
          </div>
        )}

        {/* Description */}
        {v.description && (
          <div data-testid="vehicle-description" className="text-ink-soft leading-relaxed mt-8 whitespace-pre-line break-words">{v.description}</div>
        )}

        {/* Features */}
        {v.features?.length > 0 && (
          <div className="mt-8">
            <h3 className="font-serif text-2xl mb-4">Dotazioni</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-4">
              {v.features.map((f, i) => (
                <div key={i} className="flex items-start gap-2.5 text-sm">
                  <span className="w-5 h-5 rounded-full bg-forest/10 flex items-center justify-center"><Check size={12} className="text-forest" strokeWidth={2} /></span>
                  {f}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Secondary contact */}
        <a href={`https://wa.me/${WHATSAPP_NUMBERS[0].number}?text=${encodeURIComponent(`Ciao Fantini Next Mobility! Sono interessato a ${v.make} ${v.model}.`)}`}
          target="_blank" rel="noreferrer" data-testid="detail-contact" onClick={() => track("click_whatsapp", { label: "scheda", vehicle_id: v.id })}
          className="flex items-center justify-between mt-8 p-5 bg-white rounded-2xl shadow-ambient hover:-translate-y-0.5 transition-transform">
          <div className="flex items-center gap-3">
            <MessageCircle size={20} strokeWidth={1.5} className="text-forest" />
            <div>
              <div className="text-sm font-medium">Preferisci parlarne?</div>
              <div className="text-xs text-ink-soft">Scrivi su WhatsApp a un consulente Fantini</div>
            </div>
          </div>
          <ChevronRight size={18} className="text-ink-soft" />
        </a>
      </div>

      {/* Sticky CTA */}
      {!sold && (
        <div className="fixed bottom-0 left-0 right-0 z-30 glass border-t border-border">
          <div className="max-w-3xl mx-auto px-5 py-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-xs text-ink-soft">{v.make} {v.model}</div>
              <div className="font-serif text-xl leading-none">{euro(v.price)}</div>
            </div>
            <button data-testid="start-journey-btn" onClick={() => setShowWizard(true)}
              className="btn-glow flex-[1.6] h-14 rounded-2xl bg-ink text-white flex items-center justify-center gap-2 overflow-hidden hover:opacity-90 transition-opacity active:scale-[0.98]">
              <Sparkles size={18} strokeWidth={1.5} className="text-sand" /> Crea il tuo preventivo gratuito
            </button>
          </div>
        </div>
      )}
      {sold && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-ink text-white/70 text-center py-4 text-sm">Vettura venduta</div>
      )}

      {!showWizard && (
        <ContactWidget bottom="bottom-24" message={`Ciao Fantini Next Mobility! Sono interessato a ${v.make} ${v.model}.`} />
      )}

      <AnimatePresence>
        {showWizard && (
          <Wizard vehicle={v} config={config} onClose={() => { setShowWizard(false); load(); }} onBlocked={load} />
        )}
      </AnimatePresence>
    </div>
  );
}
